import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Wordmark } from "@/components/Wordmark";

/**
 * RebrandBanner — Iter 17.
 *
 * Renders the "We're now Symplee" soft-landing card when the server-side
 * gate `user.should_show_rebrand_banner` is true. The gate already factors
 * in created_at vs REBRAND_ANNOUNCE_DATE, the dismiss flag, and the 7-day TTL,
 * so the frontend is just a thin renderer — no time math here.
 *
 * On first paint we POST /api/me/rebrand-banner/seen to stamp the first
 * impression (server is idempotent). Dismiss posts /dismiss and flips the
 * user object optimistically.
 *
 * Visual treatment matches the trial banner on AppHome — `glass-heavy`
 * card + gradient top-rail — so it lives inline above the trial card.
 */
export function RebrandBanner() {
  const { user, setUser } = useAuth();
  const [dismissing, setDismissing] = useState(false);
  const visible = !!user?.should_show_rebrand_banner;

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        await api.post("/me/rebrand-banner/seen");
      } catch (_e) { /* fail open; user will see again next page-load */ }
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [visible]);

  if (!visible) return null;

  const onDismiss = async () => {
    setDismissing(true);
    // Optimistic — flip the gate immediately so the card animates out.
    setUser((u) => (u ? { ...u, should_show_rebrand_banner: false } : u));
    try {
      await api.post("/me/rebrand-banner/dismiss");
    } catch (e) {
      // Roll the optimistic update back so the user can try again.
      setUser((u) => (u ? { ...u, should_show_rebrand_banner: true } : u));
      toast.error(errorMessage(e));
    } finally {
      setDismissing(false);
    }
  };

  return (
    <AnimatePresence initial={false}>
      <motion.div
        initial={{ opacity: 0, y: -10, height: 0 }}
        animate={{ opacity: 1, y: 0, height: "auto" }}
        exit={{ opacity: 0, y: -10, height: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="glass-heavy relative mt-8 overflow-hidden rounded-2xl p-6 sm:p-7"
        data-testid="rebrand-banner"
      >
        <div className="absolute inset-x-0 top-0 h-0.5 brand-bg" />
        <button
          type="button"
          onClick={onDismiss}
          disabled={dismissing}
          aria-label="Dismiss rebrand notice"
          className="absolute right-4 top-4 rounded-md p-1.5 text-white/45 transition-colors hover:bg-white/[0.06] hover:text-white/80"
          data-testid="rebrand-banner-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
          <div className="flex shrink-0 items-center" data-testid="rebrand-banner-wordmark">
            <Wordmark variant="dark" size="md" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-base leading-relaxed text-white/85 sm:text-[15px]" data-testid="rebrand-banner-body">
              <span className="font-medium text-white">We're now Symplee.</span> Same team. Same pricing. Same product. Just a name that finally matches the promise — <span className="brand-text">AI made simple</span>.{" "}
              <span className="text-white/55">Wordmark, login URL, and email sender all updated. Nothing else changes.</span>
            </p>
            <div className="mt-4">
              <button
                type="button"
                onClick={onDismiss}
                disabled={dismissing}
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-sm font-medium text-black transition-colors hover:bg-zinc-200 disabled:opacity-60"
                data-testid="rebrand-banner-got-it"
              >
                Got it
                <span aria-hidden="true">✓</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
